import { analyticsServices } from './services/analytics';
import { getUrlParameter } from './helpers';

export default class Analytics {
  constructor(mediaID) {
    this.mediaID = mediaID;
    this.videoAnalyticsData = {};
    this.pageAnalyticsData = {};
  }

  getPageAnalyticsData = async () => {
    const source = getUrlParameter('source');
    const analyticsResponse = await analyticsServices.getPageAnalyticsMetadata(
      this.mediaID,
      source
    );
    return analyticsResponse.data && !analyticsResponse.data.error
      ? analyticsResponse.data
      : {};
  };

  getVideoAnalyticData = async () => {
    const analyticsResponse = await analyticsServices.getVideoAnalyticsMetadata(
      this.mediaID
    );
    return analyticsResponse.data && !analyticsResponse.data.error
      ? analyticsResponse.data
      : {};
  };

  init = async () => {
    this.videoAnalyticsData = await this.getVideoAnalyticData();
    this.pageAnalyticsData = await this.getPageAnalyticsData();
    this.initGlobalVariables();

    if (typeof window._satellite != 'undefined') {
      window._satellite.track('pageview');
    }

    return {
      videoAnalyticsData: this.videoAnalyticsData,
      pageAnalyticsData: this.pageAnalyticsData,
    };
  };

  initGlobalVariables = () => {
    window.mcDataLayer = initMCDataLayer(this.pageAnalyticsData);
    window.MediaInfo = initMediaInfo(this.videoAnalyticsData);
  };

  omniLoadReady = () => {
    return window.MediaInfo && typeof window.omniLoad === 'function';
  };

  wrapPlayer = (player) => {
    // Player object to return should be consistent with the object returned
    // from mewatch.sg main site for omniload to track correctly
    return {
      player,
      addEventListener(type, callback) {
        player.addEventListener(type, callback);
      },
      removeEventListener(type, callback) {
        player.removeEventListener(type, callback);
      },
    };
  };

  callOmniload = (player) => {
    const WAITING_INTERVAL = 200;
    const KALTURA_PLAYER_VERSION = 'KL1';

    const doCall = () => {
      window.omniLoad(this.wrapPlayer(player), KALTURA_PLAYER_VERSION);
    };

    if (!this.omniLoadReady()) {
      const waiter = () => {
        if (this.omniLoadReady()) {
          doCall();
          clearTimeout(waiterTimer);
        } else {
          waiterTimer = setTimeout(waiter, WAITING_INTERVAL);
        }
      };
      let waiterTimer = setTimeout(waiter, WAITING_INTERVAL);
    } else {
      doCall();
    }
  };
}

function initMediaInfo(analyticsParams) {
  let {
    omniture,
    comscore,
    gfk,
    cxense,
    lotame,
    gam,
    youbora: youboraData,
  } = analyticsParams;
  const adobeVideoData = mapAdobeVideoData(omniture);
  const gamData = mapGamVideoData(gam);
  const youbora = mapYouboraVideoData(youboraData);

  return {
    omniture: adobeVideoData,
    gam: gamData,
    comscore,
    gfk,
    cxense,
    lotame,
    youbora,
  };
}

function initMCDataLayer(analyticsParams) {
  const { omniture, cxense, lotame, comscore } = analyticsParams;
  const adobePageData = mapAdobePageData(omniture);

  return {
    omniture: adobePageData,
    cxense,
    lotame,
    comscore,
  };
}

function mapAdobePageData(omniture) {
  return Object.assign(omniture, {
    channel: omniture.channel,
    pagename: omniture.pagename,
    language: omniture.language,
    contentid: 'NA',
    contentname: 'NA',
    contentpublishdate: 'NA',
    contentlength: 'NA',
    loggedinstatus: 'False',
    loginstatus: 'False',
    ssoid: 'NA',
    loginsource: 'NA',
    usertype: 'NA',
  });
}

function mapAdobeVideoData(omniture) {
  // Mapped as per https://wiki.massiveinteractive.com/display/MEDTOG/DC7+-+Adobe+Analytics+Data+Mapping
  return Object.assign(omniture, {
    ['mediatype']: omniture.mediatype,
    ['mediaid']: omniture.contentid,
    ['mediatitle']: omniture.mediatitle,
    ['mediaseriestitle']: omniture.mediaseriesname,
    ['mediacategory']: omniture.mediacontenttype,
    ['mediapublishdate']: omniture.mediapublishdate,
    ['mediaurl']: omniture.mediaurl,
    ['referenceid']: omniture.mediareferenceid,
    ['masrefid']: omniture.mediamasrefid,
    ['mediagenre']: omniture.mediacontenttype,
    ['mediaclassification']: omniture.mediaclassification,
    ['info']: omniture.mediainfo,
    ['loginstatus']: 'NA',
    ['language']: 'en',
    ['loggedinstatus']: 'False',
    ['ssoid']: 'NA',
    ['loginsource']: 'NA',
    ['usertype']: 'NA',
  });
}

function mapGamVideoData(gam) {
  // Mapped as per https://wiki.massiveinteractive.com/display/MEDTOG/DC15+-+Ads+Data+Mapping
  return Object.assign(gam, {
    medialanguage: gam.medialanguage,
    mediarights: gam.mediarights,
    genre1: gam.genre1,
    genre2: gam.genre2,
    genre3: gam.genre3,
    mediaid: gam.mediaid,
    seriestitle: gam.seriestitle,
    seriesid: gam.seriesid,
    mediatitle: gam.mediatitle,
    noadflag: gam.noadflag,
    profiletype: gam.profiletype,
    kidscontent: gam.kidscontent,
    product: 'NA',
  });
}

export function mapYouboraVideoData(youboraData) {
  if (!youboraData) {
    return;
  }
  const { media, extraParams } = youboraData;
  const { title, isLiveFlag, duration: durationData, resource } = media;

  const isLive =
    isLiveFlag !== undefined ? isLiveFlag.toString() === 'true' : 'NA';

  const duration = Number.parseInt(durationData, 10) || 0;

  let param5 = 'NA';
  if (extraParams.param5) {
    param5 = extraParams.param5;
  }

  return Object.assign(youboraData, {
    media: {
      title,
      duration,
      isLive,
      resource,
    },
    user: 'NA',
    username: 'NA',
    extraParams: {
      param5,
    },
  });
}
